import {
    addMinutes,
    compareDesc,
    format,
    isAfter,
    isBefore,
    parseISO,
} from "date-fns";
import { useTranslation } from "react-i18next";
import { MASK_DATE_BR, MASK_TIMESTAMP } from "../constants";
import { IDataTach } from "../../context/interfaces/IAdministratorTachContext";

const capitalizeFirstLetter = (text: string): string => {
    if (text && text.length) {
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    return text;
};

const capitalizeFirstAndToLower = (text: string): string => {
    let textFinal = text;
    if (text && text.length) {
        textFinal = text.toLowerCase();
        return textFinal.charAt(0).toUpperCase() + textFinal.slice(1);
    }

    return text;
};

const translate = (
    value: string,
    capitalize?: "captalize-first" | "not-captalize"
): string => {
    const { t } = useTranslation();
    if (capitalize === "not-captalize") {
        return t(value);
    }
    return capitalizeFirstLetter(t(value));
};

const getQueryParams = (param?: object): string => {
    const params = new URLSearchParams();
    if (param && typeof param === "object") {
        Object.keys(param).forEach((field) => {
            const item = param[field as keyof object];
            if (item) {
                params.append(field, item);
            }
        });
    }
    return params.toString() ? `?${params.toString()}` : "";
};

/**
 * Format date in string
 * @param dateString Date in string
 * @param maskDateString Mask that will be applied. (Default mask: dd/MM/yyyy)
 */
const formatDateString = (
    dateString: string,
    maskDateString = MASK_DATE_BR
): string => {
    return dateString ? format(parseISO(dateString), maskDateString) : "";
};

/**
 * Format date in string
 * @param dateString Date in string
 * @param maskDateString Mask that will be applied. (Default mask: dd/MM/yyyy)
 */
const formatDateValueString = (
    dateString: Date,
    maskDateString = MASK_DATE_BR
): string => {
    return dateString ? format(dateString, maskDateString) : "";
};

/**
 * Format date in string
 * @param data Date in string
 * @param maskDateString Mask that will be applied. (Default mask: dd/MM/yyyy)
 */
const formatDateValueStringUTC = (
    data: Date,
    maskDate = MASK_DATE_BR
): string => {
    return data
        ? format(addMinutes(data, data.getTimezoneOffset()), maskDate)
        : "";
};

function isBeforeCurrentDateTime(
    dateString: string,
    maskDate = MASK_TIMESTAMP
) {
    const date = parseISO(dateString?.trim());
    const currentDate = parseISO(format(new Date(), maskDate));
    return dateString && date ? compareDesc(date, currentDate) > 0 : false;
}

const isBeforeCurrentDate = (date: string, maskDate = MASK_TIMESTAMP) => {
    return isBefore(parseISO(format(new Date(), maskDate)), parseISO(date));
};

const isAfterCurrentDate = (date: string, maskDate = MASK_TIMESTAMP) => {
    return isAfter(parseISO(format(new Date(), maskDate)), parseISO(date));
};

const convertFilterAdminTachToQueryString = (
    list: IDataTach,
    referenceFilter: string[],
    referenceKey: string[],
    referenceQuery: string[]
) => {
    let str = "?";

    for (let index = 0; index < referenceFilter.length; index++) {
        const referenceIndex = referenceFilter[index];
        const filterByReference = list[referenceIndex].filter(
            (row) => row.add === true
        );

        if (index > 0) str += "&";

        str += `${referenceQuery[index]}=`;

        const key = referenceKey[index];

        for (
            let indexFiltered = 0;
            indexFiltered < filterByReference.length;
            indexFiltered++
        ) {
            str += `${filterByReference[indexFiltered][key]}`;

            if (indexFiltered !== filterByReference.length - 1) str += ",";
        }
    }

    return str;
};

export default {
    capitalizeFirstLetter,
    capitalizeFirstAndToLower,
    translate,
    getQueryParams,
    formatDateString,
    formatDateValueString,
    formatDateValueStringUTC,
    isBeforeCurrentDateTime,
    isBeforeCurrentDate,
    isAfterCurrentDate,
    convertFilterAdminTachToQueryString,
};
